import styled, { css } from 'styled-components';
import { Dropdown } from 'reactstrap';
import { fadeIn } from '../../../../shared';
import { mantineTheme as theme } from '../../../../theme';

const StyledNavbar = styled.nav`
  font-size: 18px;
  position: fixed;
  width: 100%;
  height: 84px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  right: 0;
  left: 0;
  top: 0;
  z-index: 1030;
  background-color: #db011c;
  box-shadow: 0 0 7px #79828A;
`;

const PlainAnchor = styled.a`
  textDecoration: inherit;
  color: inherit;
  cursor: pointer;

  :hover {
    text-decoration: none;
    color: #F7D4D8 !important;
  }
`;

const StyledLogo = styled.div`
  text-decoration: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 22px;
  margin-left: 20px;
  align-items: center;
  display: flex;
  
  @media (min-width: 768px) {
    margin-top: 0;
  };
`;

const StyledDropdown = styled(Dropdown)`
  @media (min-width: 768px) {
    margin-left: 40px;
  }

  button {
    background-color: transparent;
    border-color: transparent;
    letter-spacing: .05em;
    font-size: 18px;
    user-select: none;
    overflow: visible;
    color: #fff; 
    transition: color 0.15s ease-in-out;

    &:hover {
      color: #F7D4D8;
    }

    &:focus {
      outline: none;
    }

    &:after {
      vertical-align: 0.175em;
      border-top: 0.35em solid;
      border-right: 0.35em solid transparent;
      border-left: 0.35em solid transparent;
    }
  }

  .dropdown-menu {
    border: none;
    border-radius: 0;
    box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 11px hsla(0, 0%, 0%, 0.1);

    > button {
      font-size: 1rem;
      color: #212529;

      &:hover {
        background-color: #ffdce0;
      }

      &:focus {
        color: #212529;
      }
    }
  }
`;

const SystemDrawerWrapper = styled.div`
  hr {
    margin: 5px 50px 5px 0px;
  }

  button {
    all: unset;
    color: inherit;
    border: none;
    background-color: transparent;
    padding: 0;
    margin-bottom: 5px;
    font-size: 18px;
    letter-spacing: .05em;
  }

  .collapse button,
  .collapsing button {
    margin: 0 10px;
    font-size: 80%;
  }
`;

const StyledUserDropdown = styled(StyledDropdown)`
  button {
    &:after {
      vertical-align: 0.45em;
    }

    > svg {
      font-size: 1.85rem;
      user-select: none;
    }

    @media (max-width: ${theme.breakpoints.sm}) {
      padding: 0;
      color: #db011c;
      text-align: left;
    }
  }
`;

const bulkEntryValidProps = ({ isBulkEntryValid }) => {
  return !isBulkEntryValid
    ? css`cursor: not-allowed;`
    : css`
        cursor: pointer;
        :hover {
          color: #F7D4D8 !important;
        }
      `;
}

const BulkEntryIconWrapper = styled.div`
  ${props => bulkEntryValidProps(props)}
  text-align: center;
  padding-right: 40px;
  padding-left: 20px;
  border-right: 2px solid rgba(255,255,255,.65);
  user-select: none;
  transition: color 0.15s ease-in-out;
  color: #fff;

  > svg {
    font-size: 1.7rem;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: 0;
    color: #db011c;
    text-align: left;
  }
`;

const BulkEntryIconLabel = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
`;

const overlayPosition = ({ active }) => {
  if (active) {
    return css`
      left: 0;
      opacity: 1;
    `
  } else {
    return css`
      left: -100%;
      opacity: 0;
    `
  }
};

const Overlay = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 84px;
  z-index: -2;
  content: '';
  background-color: rgba(0,0,0,.5);
  transition: left 0s,opacity .3s linear;
  ${props => overlayPosition(props)}
`;

const Toggle = styled.button`
  margin-left: auto !important;
  @media (min-width: 768px) {
    display: none !important;
  }
`;

const List = styled.ul`
  list-style-type: none;
  margin: 0;
    
  @media (min-width: 768px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0;
  }
`;

const NavItem = styled.li`
  margin: 15px auto;
  @media (min-width: 768px) {
    margin: 0;
  }
`;

const NavLink = styled.span`
  letter-spacing: .05em;
  font-size: 18px;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  color: #db011c;
  transition: color 0.15s ease-in-out;

  :hover {
    text-decoration: none;
    color: #F7D4D8 !important;
  }

  @media (min-width: 768px) {
    margin: 0 40px;
    color: #fff !important; 
  }
`;

const navDisplay = ({ active }) => {
  if (active) {
    return css`
      height: 100%!important;
      transform: translateX(-280px);
    `;
  } else {
    return css`transform: translateX(0);`;
  }
};

const Nav = styled.nav`
  ${props => navDisplay(props)}
  animation: ${fadeIn} .4s both ease-in-out;
  justify-content: space-between;
  width: 280px;
  height: 100%!important;
  position: fixed;
  top: 84px;
  right: -280px;
  background-color: white;
  transition: transform .25s linear;
  will-change: transform;
  margin: 0;
  
  @media (min-width: 768px) {
    background-color: #db011c !important;
    display: flex;
    margin-right: 30px;
    flex-direction: row;
    position: relative;
    top: 0;
    right: 0;
    width: auto;
    align-items: center;
    transform: translateX(0);
    transition: transform .25s linear;
    width: 100%;
  }
`;

export {
  Nav,
  NavLink,
  NavItem,
  Overlay,
  Toggle,
  List,
  StyledLogo,
  StyledNavbar,
  StyledDropdown,
  BulkEntryIconLabel,
  SystemDrawerWrapper,
  StyledUserDropdown,
  BulkEntryIconWrapper,
  PlainAnchor,
};